/**
 * SAT Cinematic Intro · Zentrale Konfiguration
 *
 * Feature-Flags werden zur BUILD-TIME aus process.env gelesen und zur RUNTIME
 * aus window.localStorage / URL-Params überschrieben (nur für Preview-Debug).
 *
 * WICHTIG:
 *   – Kein Auto-Enable in Production.
 *   – Kein Autoplay-Sound.
 *   – Kein zusätzliches Tracking.
 *   – Standardmässig OFF; Preview via ENV aktivieren.
 */

const bool = (v, fallback = false) => {
  if (v === undefined || v === null || v === "") return fallback;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
};

// --- BUILD-TIME (aus .env) -------------------------------------------------
const ENV_ENABLED = bool(process.env.REACT_APP_SAT_CINEMATIC_INTRO_ENABLED, false);
const ENV_MODE = String(process.env.REACT_APP_SAT_CINEMATIC_INTRO_MODE || "session").toLowerCase();
const ENV_MOBILE = bool(process.env.REACT_APP_SAT_CINEMATIC_INTRO_MOBILE, true);
const ENV_REDUCED_MOTION = bool(process.env.REACT_APP_SAT_CINEMATIC_INTRO_REDUCED_MOTION, true);

// --- Timeline (in Sekunden) ------------------------------------------------
// Gesamt-Zieldauer ca. 6.4–7.0s (Max 8.0s)
export const TIMELINE = {
  DUST_IN:      { start: 0.00, end: 0.70 },  // Naturraum + erste Goldpartikel
  GATHER:       { start: 0.70, end: 1.70 },  // Partikel finden Zentrum
  EMERGE:       { start: 1.70, end: 2.80 },  // Kolibri entsteht aus Licht
  AWAKEN:       { start: 2.80, end: 3.40 },  // Erster Flügelschlag
  NOTICE:       { start: 3.40, end: 4.00 },  // Head-Turn Signature: "Er hat mich wahrgenommen"
  BRAND:        { start: 3.00, end: 4.20 },  // "SAT AKADEMIE" materialisiert
  FLIGHT:       { start: 4.00, end: 5.00 },  // Kolibri startet organischen Flug
  ACCELERATE:   { start: 5.00, end: 5.70 },  // Beschleunigung Richtung Kamera
  RIFT:         { start: 5.70, end: 6.40 },  // Transformations-Riss öffnet
  REVEAL:       { start: 6.40, end: 7.00 },  // Homepage wird sichtbar
  TOTAL_MS:     7000,
};

// --- Reduced Motion Timeline (kürzer, ruhig) -------------------------------
export const REDUCED_TIMELINE = {
  DUST_IN:    { start: 0.00, end: 0.40 },
  EMERGE:     { start: 0.40, end: 1.00 },
  BRAND:      { start: 1.00, end: 2.20 },
  REVEAL:     { start: 2.20, end: 3.00 },
  TOTAL_MS:   3000,
};

// --- Skip-/Failsafe --------------------------------------------------------
export const SKIP_CROSSFADE_MS = 320;
export const FAILSAFE_MAX_WAIT_MS = 2500;   // wenn Assets/Frame nicht laufen
export const SESSION_STORAGE_KEY = "sat_cinematic_intro_played_v1";

// --- Farben (SAT-Palette) --------------------------------------------------
export const PALETTE = {
  BG_DEEP:      "#050914",   // fast schwarzes Nachtblau
  BG_MID:       "#0a1428",   // tiefes Blau
  BG_ACCENT:    "#1a1436",   // dezent violettverschoben
  GOLD:         "#f4c752",   // SAT Gold
  GOLD_WARM:    "#e8b23a",   // wärmeres Gold
  GOLD_LIGHT:   "#fce8a8",   // helles Cremegold
  CREAM:        "#f6efd8",   // warmes Cremeweiss
  NATURE:       "#3a5a3f",   // dezentes Grün
};

// --- Runtime Overrides (nur Preview / Debug) -------------------------------
const getRuntimeOverride = (key) => {
  if (typeof window === "undefined") return undefined;
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get(`intro_${key}`);
    if (q !== null) return q;
    const ls = window.localStorage.getItem(`sat_intro_${key}`);
    if (ls !== null) return ls;
  } catch (_e) { /* noop */ }
  return undefined;
};

export const getIntroConfig = () => {
  const enabledOverride = getRuntimeOverride("enabled");
  const modeOverride = getRuntimeOverride("mode");
  return {
    enabled: enabledOverride !== undefined ? bool(enabledOverride, ENV_ENABLED) : ENV_ENABLED,
    mode: (modeOverride || ENV_MODE).toLowerCase(),   // "always" | "session" | "first_visit" | "disabled"
    mobile: ENV_MOBILE,
    respectReducedMotion: ENV_REDUCED_MOTION,
  };
};

/**
 * Entscheidet ob das Intro auf der aktuellen Navigation gespielt werden darf.
 * - Nur Root-Route "/"
 * - Nur bei Direct-Visit / Reload (nicht bei SPA-interner Navigation)
 * - Mode-abhängig (session / always / first_visit / disabled)
 */
export const shouldPlayIntro = (pathname) => {
  if (typeof window === "undefined") return false;
  if (pathname !== "/") return false;

  const cfg = getIntroConfig();
  if (!cfg.enabled) return false;
  if (cfg.mode === "disabled") return false;

  // Nur bei Direct-Visit / Reload: performance.navigation ist deprecated,
  // stattdessen Performance API oder Session-Flag verwenden.
  // Wir merken uns, ob der Intro-Bootstrapper diesen Tab schon einmal
  // durchlaufen hat. Wenn ja, ist es eine SPA-Navigation → NICHT spielen.
  const bootedThisSession = window.sessionStorage.getItem(`${SESSION_STORAGE_KEY}_booted`) === "1";

  if (cfg.mode === "always") {
    return !bootedThisSession;   // wenigstens einmal pro Tab-Boot
  }
  if (cfg.mode === "session") {
    if (bootedThisSession) return false;
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY) !== "1";
  }
  if (cfg.mode === "first_visit") {
    if (bootedThisSession) return false;
    return window.localStorage.getItem(SESSION_STORAGE_KEY) !== "1";
  }
  return false;
};

export const markIntroPlayed = () => {
  if (typeof window === "undefined") return;
  const cfg = getIntroConfig();
  window.sessionStorage.setItem(`${SESSION_STORAGE_KEY}_booted`, "1");
  if (cfg.mode === "session") {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, "1");
  } else if (cfg.mode === "first_visit") {
    window.localStorage.setItem(SESSION_STORAGE_KEY, "1");
  }
};
